import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, ExternalLink, Pencil, X } from "lucide-react";
import { api, errMsg, resolveMedia } from "@/lib/api";
import { AnimeForm } from "@/components/admin/AnimeForm";

export const AnimeManager = ({ list, onChanged }) => {
  const [editing, setEditing] = useState(null);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}" and all its episodes?`)) return;
    try {
      await api.delete(`/anime/${a.id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-anime-manager" className="kp-glass p-7 kp-card-clip">
      <span className="kp-eyebrow">Library</span>
      <h2 className="font-display font-bold text-xl mt-1 mb-5">Your Anime · {list.length}</h2>
      <div className="flex flex-col divide-y divide-white/[0.06] max-h-[420px] overflow-y-auto">
        {list.map((a) => (
          <div key={a.id} data-testid={`manager-row-${a.id}`} className="flex items-center gap-4 py-3">
            <img src={resolveMedia(a.cover_url)} alt="" className="w-10 h-14 object-cover kp-card-clip" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold truncate">{a.title}</div>
              <div className="text-xs font-mono text-[#64748B]">{a.episode_count} episodes · {a.genres.join(", ")}</div>
            </div>
            <button onClick={() => setEditing(a)} className="p-2 text-[#94A3B8] hover:text-[#00F0FF] transition-colors" data-testid={`manager-edit-${a.id}`} aria-label="Edit"><Pencil size={16} /></button>
            <Link to={`/anime/${a.id}`} className="p-2 text-[#94A3B8] hover:text-[#00F0FF] transition-colors" data-testid={`manager-open-${a.id}`}><ExternalLink size={16} /></Link>
            <button onClick={() => remove(a)} className="p-2 text-[#94A3B8] hover:text-[#FF2A55] transition-colors" data-testid={`manager-delete-${a.id}`} aria-label="Delete"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {createPortal(
        <AnimatePresence>
          {editing && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)} className="fixed inset-0 z-[70] bg-[#08070C]/80 backdrop-blur-sm" />
              <motion.div
                data-testid="edit-anime-modal"
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                className="fixed z-[80] inset-x-4 top-[6vh] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[560px] max-h-[88vh] overflow-y-auto bg-[#0F0D16] border border-white/[0.08] p-7"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="kp-eyebrow">Edit</span>
                    <h2 className="font-display font-bold text-xl mt-1 truncate max-w-[380px]">{editing.title}</h2>
                  </div>
                  <button data-testid="edit-anime-close" onClick={() => setEditing(null)} className="p-2 text-[#94A3B8] hover:text-white transition-colors" aria-label="Close"><X size={18} /></button>
                </div>
                <AnimeForm key={editing.id} initial={editing} onSaved={() => { setEditing(null); onChanged?.(); }} />
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};
